// „Zu tun“ (E3c, S2 – an Stelle von „Zu prüfen“; G18: „wo bei wem was noch
// fehlt“, G19: selbsterklärend): EINE Liste, die älteste gesendete
// Besichtigung zuerst. Je Anlage eine Karte – wer, wo, wann montiert wird,
// ein Satz, was als Nächstes kommt, und NUR die Schritte, die jetzt gehen,
// als Knöpfe. Was nach der Freigabe dazukam („bitte ansehen“, Nachtrag),
// steht weiter sichtbar auf der Karte.
//
// Darunter je Karte, sobald freigegeben ist, „alles bis zur Montage schon
// erledigt“ – für Anlagen, die Manuel vor dem Update ohne System bestellt und
// eingeteilt hat; mit einer Rückfrage in einer Zeile.
//
// Die Liste kommt vom Server (GET /api/crm/zu-tun), die Kette je Anlage
// rechnet der Kern; hier steht nur, wie es aussieht (zutun.ts: Knöpfe und Satz).

import { useEffect, useState } from "react";
import { schonErledigtMoeglich, type SchonErledigtAntwort, type ZuTunAntwort, type ZuTunKarte } from "@happy-energy/kern";
import { Chip, Ikon, senden, Tipp, toastZeigen } from "@happy-energy/oberflaeche";
import { kalendertagZeigen, wannZeigen } from "../anzeige.ts";
import { fehlerText, Laedt, LadeFehler, useLaden } from "../laden.tsx";
import { Seite } from "../rahmen.tsx";
import { anderenFensternMelden, useAmServerGeaendert } from "../speicher.ts";
import { knoepfe, naechsterSatz } from "./zutun.ts";
import "./zutun.css";

/** „Alles bis zur Montage schon erledigt“ – erst ein Link, dann die Rückfrage in einer Zeile. */
function SchonErledigt({ k, fertig }: { k: ZuTunKarte; fertig: () => void }) {
  const [fragen, setFragen] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function vermerken() {
    setSendet(true);
    setFehler(null);
    try {
      const antwort = await senden<SchonErledigtAntwort>(`/api/crm/anlage/${k.anlageId}/schon-erledigt`);
      toastZeigen(antwort.ok && !antwort.geaendert ? "War schon vermerkt." : "Vermerkt – Bestellung, Dachpartie und Bodenpartie sind erledigt.");
      anderenFensternMelden(k.anlageId);
      fertig();
    } catch (f) {
      setFehler(fehlerText(f));
      setSendet(false);
    }
  }

  if (!fragen) {
    return (
      <button type="button" className="crm-knopf leise klein zutun-schon" onClick={() => setFragen(true)}>
        alles bis zur Montage schon erledigt
      </button>
    );
  }
  return (
    <div className="zutun-rueckfrage" role="group" aria-label="Schon erledigt?">
      <span>Bestellung, Dachpartie und Bodenpartie wurden schon ohne System erledigt?</span>
      <button type="button" className="crm-knopf dunkel klein" onClick={() => void vermerken()} disabled={sendet}>
        Ja, als erledigt vermerken
      </button>
      <button type="button" className="crm-knopf rand klein" onClick={() => setFragen(false)} disabled={sendet}>
        Abbrechen
      </button>
      {fehler && (
        <span className="zutun-fehler" role="alert">
          {fehler}
        </span>
      )}
    </div>
  );
}

function Karte({ k, neuLaden }: { k: ZuTunKarte; neuLaden: () => void }) {
  const liste = knoepfe(k);
  return (
    <article className="crm-karte zutun-karte" aria-label={k.kundeName}>
      <div className="zutun-kopf">
        <div className="zutun-wer">
          <a href={`#/anlage/${k.anlageId}`}>
            <b>{k.kundeName}</b>
          </a>
          <span>
            {k.strasse}, {k.plz} {k.ort}
          </span>
        </div>
        <span className={`zutun-termin${k.montagetermin.am ? "" : " fehlt"}`}>
          <Ikon name="kalender" groesse="k" /> {k.montagetermin.am ? `Montage ${kalendertagZeigen(k.montagetermin.am)}` : "noch kein Montagetermin"}
        </span>
      </div>

      <p className="zutun-satz">{naechsterSatz(k)}</p>

      {(k.hinweis !== null || k.offeneFragen > 0) && (
        <div className="chip-zeile">
          {k.hinweis === "bitte-ansehen" && (
            <a href={`#/freigabe/${k.aufnahmeId}`} className="zutun-signal">
              <Chip art="warn" ikon="auge">
                bitte ansehen – nach der Freigabe kam etwas dazu
              </Chip>
            </a>
          )}
          {k.hinweis === "nachtrag" && (
            <Chip art="lime" ikon="plus">
              mit Nachtrag
            </Chip>
          )}
          {k.offeneFragen > 0 && (
            <Chip art="frage" ikon="frage">
              {k.offeneFragen === 1 ? "1 offene Frage" : `${k.offeneFragen} offene Fragen`}
            </Chip>
          )}
        </div>
      )}

      {liste.length > 0 && (
        <ul className="zutun-knoepfe" aria-label="Was jetzt geht">
          {liste.map((knopf) => (
            <li key={knopf.schritt}>
              <a href={`#${knopf.pfad}`} className={`crm-knopf klein ${knopf.schritt === "freigeben" ? "lime" : "dunkel"}`}>
                {knopf.wort} <Ikon name="rechts" groesse="k" />
              </a>
              {knopf.grund && <span className="zutun-grund">{knopf.grund}</span>}
            </li>
          ))}
        </ul>
      )}

      <div className="zutun-fuss">
        <span className="leise">gesendet {wannZeigen(k.gesendetAm)}</span>
        {schonErledigtMoeglich(k.kette) && <SchonErledigt k={k} fertig={neuLaden} />}
      </div>
    </article>
  );
}

/** Frisch halten: jede Minute, solange die Seite vorne ist, und sobald sie wieder nach vorne kommt (wie die Übersicht). */
function useFrisch(neu: () => void): void {
  useAmServerGeaendert(neu);
  useEffect(() => {
    const vorne = () => document.visibilityState === "visible";
    const uhr = window.setInterval(() => {
      if (vorne()) neu();
    }, 60_000);
    const sichtbar = () => {
      if (vorne()) neu();
    };
    document.addEventListener("visibilitychange", sichtbar);
    return () => {
      window.clearInterval(uhr);
      document.removeEventListener("visibilitychange", sichtbar);
    };
  }, [neu]);
}

export function ZuTunSeite() {
  const { daten, fehler, neu } = useLaden<ZuTunAntwort>("/api/crm/zu-tun");
  useFrisch(neu);
  const liste = daten?.anlagen ?? [];

  return (
    <Seite
      titel="Zu tun"
      unter={
        daten
          ? liste.length === 0
            ? "Gerade ist bei keiner Anlage etwas zu tun."
            : `Bei ${liste.length === 1 ? "1 Anlage" : `${liste.length} Anlagen`} geht jetzt etwas – die älteste zuerst. Ein Knopf führt dorthin, wo es erledigt wird.`
          : undefined
      }
    >
      {fehler !== undefined && <LadeFehler fehler={fehler} nochmal={neu} />}
      {!daten && fehler === undefined && <Laedt />}
      {daten && liste.length === 0 && (
        <Tipp>Hier steht jede Anlage, an der jetzt etwas geht – Freigeben, Bestellen, die Partien. Neue Besichtigungen erscheinen, sobald der Verkauf sie sendet.</Tipp>
      )}
      {liste.length > 0 && (
        <div className="zutun-liste">
          {liste.map((k) => (
            <Karte key={k.anlageId} k={k} neuLaden={neu} />
          ))}
        </div>
      )}
    </Seite>
  );
}
